import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Tooltip from '@mui/material/Tooltip';

export default function ImportStoresModal({
  open,
  onClose,
  posStores = [],
  existingStores = [],
  link_or_create_store_from_pos,
  archive_pos_store
}) {
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [storeToDelete, setStoreToDelete] = React.useState(null);
  const [linkModalOpen, setLinkModalOpen] = React.useState(false);
  const [storeToLink, setStoreToLink] = React.useState(null);
  const [selectedLinkStore, setSelectedLinkStore] = React.useState(null);

  const handleClose = () => {
    onClose();
  };

  const handleImportStore = async (storeUuid) => {
    console.log("CREATING NEW STORE FROM POS");
    if (link_or_create_store_from_pos) {
      // Call with null store_id to create a new store
      await link_or_create_store_from_pos(storeUuid, null);
    }
  };

  const handleDeleteClick = (store) => {
    setStoreToDelete(store);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (storeToDelete && archive_pos_store) {
      try {
        // Call the archive function with the store's UUID
        await archive_pos_store(storeToDelete.uuid);

        // Close the delete modal
        setDeleteModalOpen(false);
        setStoreToDelete(null);

        // Optionally close the main modal as well after successful archiving
        // handleClose();
      } catch (error) {
        console.error('Error archiving POS store:', error);
        // You might want to show an error message to the user here
        // For now, we'll still close the modal even if there's an error
        setDeleteModalOpen(false);
        setStoreToDelete(null);
      }
    } else {
      // Fallback: just close the modal if function isn't available
      setDeleteModalOpen(false);
      setStoreToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setStoreToDelete(null);
  };

  const handleLinkClick = (store) => {
    setStoreToLink(store);
    setLinkModalOpen(true);
    setSelectedLinkStore(null);
  };

  const handleLinkCancel = () => {
    setLinkModalOpen(false);
    setStoreToLink(null);
    setSelectedLinkStore(null);
  };

  const handleLinkConfirm = async () => {
    if (selectedLinkStore && storeToLink && link_or_create_store_from_pos) {
      console.log('Linking POS store:', storeToLink, 'to existing store:', selectedLinkStore);

      // Call with store_id to link to existing store
      await link_or_create_store_from_pos(
        storeToLink.uuid,
        selectedLinkStore.id
      );

      // Close modals after successful linking
      setLinkModalOpen(false);
      setStoreToLink(null);
      setSelectedLinkStore(null);
      handleClose(); // Close main modal too
    } else {
      // Fallback behavior
      setLinkModalOpen(false);
      setStoreToLink(null);
      setSelectedLinkStore(null);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    maxHeight: "95vh",
    overflow: "auto",
  };

  const deleteModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
  };

  const linkModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 700,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    maxHeight: "95vh",
    overflow: "auto",
  };

  const getStoreInitials = (storeName) => {
    if (!storeName) return "S";
    return storeName.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-50 border-emerald-100 text-emerald-800';
      case 'inactive':
        return 'bg-rose-50 border-rose-100 text-rose-800';
      case 'setting_up':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-gray-50 border-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="pos-stores-modal-title"
        aria-describedby="pos-stores-modal-description"
      >
        <Box sx={modalStyle}>
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div>
            <div className="mb-6 flex items-center space-x-3">
              <WarningIcon className="text-amber-600 h-8 w-8" />
              <h3 className="text-xl font-semibold text-gray-900">
                Locations from Point of Sale System
              </h3>
            </div>

            <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-amber-800 text-sm">
                The following locations exist in your point of sale system but haven't been imported yet.
                Click the plus icon to create a new location or the link icon to connect to an existing location.
              </p>
            </div>

            <div className="text-sm font-medium text-gray-700 mb-2">
              {posStores.length} locations
            </div>

            {posStores && posStores.length > 0 && (
              <div className="mb-4">
                <div className="max-h-[450px] overflow-y-auto border border-gray-200 rounded-lg">
                  {posStores.map((store) => (
                    <div
                      key={store.uuid || store.id}
                      className="flex items-center px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                    >
                      <Tooltip title="Create new location in Rightwork" placement="top">
                        <IconButton
                          onClick={() => handleImportStore(store.uuid || store.id)}
                          size="small"
                          className="text-gray-400 hover:text-emerald-600 transition-colors"
                          sx={{
                            '&:hover': {
                              color: '#059669',
                              backgroundColor: '#f3f4f6',
                            }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-violet-200 rounded-full flex items-center justify-center text-sm font-medium text-violet-700">
                            <LocationOnIcon fontSize="small" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {store.name || store.location_name}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Tooltip title="Link to existing location in Rightwork" placement="top">
                          <IconButton
                            onClick={() => handleLinkClick(store)}
                            size="small"
                            className="text-gray-400 hover:text-violet-600 transition-colors"
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Exclude from Rightwork" placement="top">
                          <IconButton
                            onClick={() => handleDeleteClick(store)}
                            size="small"
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            sx={{
                              '&:hover': {
                                color: '#ef4444',
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6">
              <button
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-store-modal-title"
        aria-describedby="delete-store-modal-description"
      >
        <Box sx={deleteModalStyle}>
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <DeleteIcon className="text-red-600" />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Exclude Location from Rightwork
            </h3>

            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to exclude this location from the import locations list?
              This action can't be undone.
            </p>

            {storeToDelete && (
              <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-center space-x-3">
                  <div className="w-8 h-8 bg-violet-200 rounded-full flex items-center justify-center text-sm font-medium text-violet-700">
                    <LocationOnIcon fontSize="small" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">
                      {storeToDelete.name || storeToDelete.location_name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <button
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 text-sm font-medium"
                onClick={handleDeleteConfirm}
              >
                Exclude
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Link Store Modal */}
      <Modal
        open={linkModalOpen}
        onClose={handleLinkCancel}
        aria-labelledby="link-store-modal-title"
        aria-describedby="link-store-modal-description"
      >
        <Box sx={linkModalStyle}>
          <div className="absolute right-2 top-2">
            <IconButton
              className="text-gray-400 hover:text-gray-600"
              onClick={handleLinkCancel}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div>
            <div className="mb-6 flex items-center space-x-3">
              <LinkIcon className="text-violet-600 h-8 w-8" />
              <h3 className="text-xl font-semibold text-gray-900">
                Link Location
              </h3>
            </div>

            {storeToLink && (
              <>
                <div className="mb-6 p-4 bg-violet-50 rounded-lg border border-violet-200">
                  <p className="text-violet-700 text-sm mb-3">
                    Link this POS location to an existing location in Rightwork:
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-violet-200 rounded-full flex items-center justify-center text-sm font-medium text-violet-700">
                      <LocationOnIcon />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {storeToLink.name || storeToLink.location_name}
                      </p>
                      <p className="text-xs text-violet-600 font-medium">From POS System</p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Existing Locations in Rightwork
                  </h4>
                  {existingStores.length > 0 ? (
                    <div className="max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg">
                      {existingStores.map((store) => (
                        <div
                          key={store.id}
                          className={`flex items-center px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors
                            ${selectedLinkStore?.id === store.id
                              ? 'bg-violet-50 border-violet-200'
                              : 'hover:bg-gray-50'}`}
                          onClick={() => setSelectedLinkStore(store)}
                        >
                          <div className="flex items-center flex-1 space-x-3">
                            <div className="w-10 h-10 bg-violet-200 rounded-full flex items-center justify-center text-sm font-medium text-violet-700">
                              <LocationOnIcon />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {store.store_name}
                              </p>
                              {/* <div className="flex items-center space-x-2 mt-1">
                                {store.latitude && store.longitude && (
                                  <p className="text-xs text-gray-500">
                                    {store.latitude}, {store.longitude}
                                  </p>
                                )}
                                {store.onboarding_status && (
                                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(store.onboarding_status)}`}>
                                    {store.onboarding_status.replace('_', ' ')}
                                  </div>
                                )}
                              </div> */}
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                              ${selectedLinkStore?.id === store.id
                                ? 'border-violet-600 bg-violet-600'
                                : 'border-gray-300'}`}>
                              {selectedLinkStore?.id === store.id && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">No locations found in Rightwork</p>
                      <p className="text-xs mt-1">You may want to import this as a new location instead.</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                    onClick={handleLinkCancel}
                  >
                    Cancel
                  </button>
                  <button
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200
                      ${selectedLinkStore
                        ? 'bg-violet-600 text-white hover:bg-violet-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                    onClick={() => selectedLinkStore && handleLinkConfirm()}
                    disabled={!selectedLinkStore}
                  >
                    Link Location
                  </button>
                </div>
              </>
            )}
          </div>
        </Box>
      </Modal>
    </>
  );
}
